import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CalendarDays,
  ChevronDown,
  MapPin,
  Minus,
  Plus,
  X,
} from "lucide-react";
import styles from "./BookingBar.module.css";
const BookingBar = () => {
  // =========================
// POPUP STATES
// =========================
const [showGuests, setShowGuests] = useState(false);

const [showSpecialRates, setShowSpecialRates] = useState(false);

const [selectedRate, setSelectedRate] = useState(
  "Lowest Regular Rate"
);

const specialRatesRef = useRef(null);

const [specialRatesPosition, setSpecialRatesPosition] = useState({
  top: 0,
  left: 0,
  width: 540,
});


// =========================
// ROOMS & GUESTS
// =========================

const [rooms, setRooms] = useState(3);
const [adults, setAdults] = useState(1);
const [children, setChildren] = useState(0);

const totalGuests = adults + children;
const maximumGuests = rooms * 8;


// =========================
// SPECIAL RATES POPUP
// =========================

const openSpecialRates = () => {
  if (!specialRatesRef.current) return;

  const rect = specialRatesRef.current.getBoundingClientRect();

  const popupWidth = 540;

  // SPECIAL RATES column right edge ki popup right edge align
  const left = rect.right - popupWidth;

  setSpecialRatesPosition({
    top: rect.bottom + 8,
    left: Math.max(10, left),
    width: popupWidth,
  });

  setShowGuests(false);
  setShowSpecialRates(true);
};


// =========================
// UPDATE SPECIAL RATE POPUP POSITION
// =========================

useEffect(() => {
  if (!showSpecialRates) return;

  const updatePosition = () => {
    if (!specialRatesRef.current) return;

    const rect = specialRatesRef.current.getBoundingClientRect();

    const popupWidth = 540;

    const left = rect.right - popupWidth;

    setSpecialRatesPosition({
      top: rect.bottom + 8,
      left: Math.max(10, left),
      width: popupWidth,
    });
  };

  updatePosition();

  window.addEventListener("resize", updatePosition);
  window.addEventListener("scroll", updatePosition, true);

  return () => {
    window.removeEventListener("resize", updatePosition);
    window.removeEventListener("scroll", updatePosition, true);
  };
}, [showSpecialRates]);

// =========================
// ROOMS
// Maximum 3 rooms
// =========================

const increaseRooms = () => {
  if (rooms < 3) {
    setRooms((prev) => prev + 1);
  }
};

const decreaseRooms = () => {
  if (
    rooms > 1 &&
    totalGuests <= (rooms - 1) * 8
  ) {
    setRooms((prev) => prev - 1);
  }
};


// =========================
// ADULTS
// =========================

const increaseAdults = () => {
  if (totalGuests < maximumGuests) {
    setAdults((prev) => prev + 1);
  }
};

const decreaseAdults = () => {
  if (adults > 1) {
    setAdults((prev) => prev - 1);
  }
};


// =========================
// CHILDREN
// =========================

const increaseChildren = () => {
  if (totalGuests < maximumGuests) {
    setChildren((prev) => prev + 1);
  }
};

const decreaseChildren = () => {
  if (children > 0) {
    setChildren((prev) => prev - 1);
  }
};


// =========================
// OPEN GUEST POPUP
// =========================

const openGuests = () => {
  setShowGuests(true);
  setShowSpecialRates(false);
};

const bookingBarRef = useRef(null);

useEffect(() => {
  let frame = 0;

  const placeBar = () => {
    const nav = document.querySelector("[data-site-nav]");
    const bar = bookingBarRef.current;
    if (nav && bar) {
      bar.style.top = `${nav.getBoundingClientRect().bottom - 1}px`;
      const main = document.querySelector("main");
      if (main) {
        const barBottom = Math.max(0, Math.ceil(bar.getBoundingClientRect().bottom) - 1);
        main.style.paddingTop = `${barBottom}px`;
      }
    }
    frame = requestAnimationFrame(placeBar);
  };

  frame = requestAnimationFrame(placeBar);
  return () => cancelAnimationFrame(frame);
}, []);

const ratesHref =
  "https://book-directonline.com/properties/southtweedmidirect?locale=en&items[0][adults]=2&items[0][children]=0&items[0][infants]=0&currency=AUD&checkInDate=2026-09-26&checkOutDate=2026-09-27&trackPage=yes";


return (
    <section className={styles.bookingWrapper} ref={bookingBarRef}>

      {/* =====================================================
          RESORT INFORMATION
      ====================================================== */}
      <div className={styles.resortInfo}>

        <div className={styles.resortName}>
          Arabella Motor Inn – Coastal Stay near Tweed Heads South
        </div>

        <a
          href="https://www.google.com/maps/place/Arabella+Motor+Inn/@-28.1880296,153.5379114,17z/data=!4m9!3m8!1s0x6b90ffc81fcd0c6d:0xba0e6aef62dcb581!5m2!4m1!1i2!8m2!3d-28.1880344!4d153.5404863!16s%2Fg%2F1tm88lxb?hl=en&entry=ttu&g_ep=EgoyMDI2MDkyMy4wIKXMDSoASAFQAw%3D%3D"
          className={styles.viewMap}
          target="_blank"
          rel="noopener noreferrer"
        >
          <MapPin size={20} strokeWidth={2} />
          <span>VIEW MAP</span>
        </a>

      </div>


      {/* =====================================================
          BOOKING OPTIONS
      ====================================================== */}
      <div className={styles.bookingOptions}>

        {/* =================================================
            DATES
        ================================================== */}
        <div className={styles.bookingItem}>

          <div className={styles.bookingLabel}>
            <CalendarDays size={18} />
            <span>DATES (1 NIGHT)</span>
          </div>

          <div className={styles.dateValue}>
            <span>Fri, Sep 25</span>
            <span className={styles.arrow}>→</span>
            <span>Sat, Sep 26</span>
          </div>

        </div>


        {/* =================================================
            ROOMS & GUESTS
        ================================================== */}
        <div className={styles.bookingItem}>

          <div className={styles.bookingLabel}>
            <span>ROOMS &amp; GUESTS</span>
          </div>

          <button
            type="button"
            className={styles.selectValue}
            onClick={openGuests}
          >

            <span>
              {rooms} {rooms === 1 ? "Room" : "Rooms"},{" "}
              {adults} {adults === 1 ? "Adult" : "Adults"}

              {children > 0 &&
                `, ${children} ${
                  children === 1 ? "Child" : "Children"
                }`}
            </span>

            <ChevronDown
              size={20}
              style={{
                transform: showGuests
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />

          </button>

        </div>


        {/* =================================================
            SPECIAL RATES
        ================================================== */}
        <div
  className={styles.bookingItem}
  ref={specialRatesRef}
>
  <div className={styles.bookingLabel}>
    <span>SPECIAL RATES</span>
  </div>

  <button
    type="button"
    className={styles.selectValue}
    onClick={openSpecialRates}
  >
    <span>{selectedRate}</span>

    <ChevronDown
      size={20}
      className={
        showSpecialRates ? styles.chevronUp : ""
      }
    />
  </button>
</div>


        {/* =================================================
            VIEW RATES
        ================================================== */}
        <a
  className={styles.viewRatesButton}
  href={ratesHref}
  target="_blank"
  rel="noopener noreferrer"
>
  View Rates
</a>

      </div>


      {/* =====================================================
          ROOMS & GUESTS POPUP
          IMPORTANT: createPortal -> document.body
          This makes it appear ABOVE the hero/card.
      ====================================================== */}
      {showGuests &&
        createPortal(
          <div
            className={styles.guestsOverlay}
            onClick={() => setShowGuests(false)}
          >

            <div
              className={styles.guestsPopup}
              onClick={(e) => e.stopPropagation()}
            >

              {/* CLOSE */}
              <button
                type="button"
                className={styles.closeGuests}
                onClick={() => setShowGuests(false)}
                aria-label="Close rooms and guests"
              >
                <X size={20} />
              </button>


              {/* TITLE */}
              <div className={styles.guestsTitle}>
                MAXIMUM 8 GUESTS PER ROOM
              </div>


              {/* =================================================
                  ROOMS
              ================================================== */}
              <div className={styles.guestRow}>

                <div className={styles.guestInfo}>

                  <div className={styles.guestName}>
                    Rooms
                  </div>

                  <div className={styles.guestLimit}>
                    (Max: 3 Rooms/person)
                  </div>

                </div>


                <div className={styles.counter}>

                  <button
                    type="button"
                    onClick={decreaseRooms}
                    disabled={
                      rooms <= 1 ||
                      totalGuests > (rooms - 1) * 8
                    }
                    className={styles.counterButton}
                  >
                    <Minus size={17} />
                  </button>


                  <span className={styles.counterValue}>
                    {rooms}
                  </span>


                  <button
                    type="button"
                    onClick={increaseRooms}
                    disabled={rooms >= 3}
                    className={styles.counterButton}
                  >
                    <Plus size={19} />
                  </button>

                </div>

              </div>


              {/* =================================================
                  ADULTS
              ================================================== */}
              <div className={styles.guestRow}>

                <div className={styles.guestInfo}>

                  <div className={styles.guestName}>
                    Adults
                  </div>

                  <div className={styles.guestLimit}>
                    (Max: 8 total guests/room)
                  </div>

                </div>


                <div className={styles.counter}>

                  <button
                    type="button"
                    onClick={decreaseAdults}
                    disabled={adults <= 1}
                    className={styles.counterButton}
                  >
                    <Minus size={17} />
                  </button>


                  <span className={styles.counterValue}>
                    {adults}
                  </span>


                  <button
                    type="button"
                    onClick={increaseAdults}
                    disabled={totalGuests >= maximumGuests}
                    className={styles.counterButton}
                  >
                    <Plus size={19} />
                  </button>

                </div>

              </div>


              {/* =================================================
                  CHILDREN
              ================================================== */}
              <div className={styles.guestRow}>

                <div className={styles.guestInfo}>

                  <div className={styles.guestName}>
                    Children
                  </div>

                  <div className={styles.guestLimit}>
                    (Max: 8 total guests/room)
                  </div>

                </div>


                <div className={styles.counter}>

                  <button
                    type="button"
                    onClick={decreaseChildren}
                    disabled={children <= 0}
                    className={styles.counterButton}
                  >
                    <Minus size={17} />
                  </button>


                  <span className={styles.counterValue}>
                    {children}
                  </span>


                  <button
                    type="button"
                    onClick={increaseChildren}
                    disabled={totalGuests >= maximumGuests}
                    className={styles.counterButton}
                  >
                    <Plus size={19} />
                  </button>

                </div>

              </div>


              {/* DONE */}
              <button
                type="button"
                className={styles.doneButton}
                onClick={() => setShowGuests(false)}
              >
                Done
              </button>

            </div>

          </div>,

          document.body
        )}


      {/* =====================================================
          SPECIAL RATES POPUP
          IMPORTANT: createPortal -> document.body
          This makes it appear ABOVE the hero/card.
      ====================================================== */}
      {showSpecialRates &&
  createPortal(
    <div
      className={styles.specialRatesOverlay}
      onClick={() => setShowSpecialRates(false)}
    >
      <div
        className={styles.specialRatesPopup}
        style={{
          position: "fixed",
          top: `${specialRatesPosition.top}px`,
          left: `${specialRatesPosition.left}px`,
          width: `${specialRatesPosition.width}px`,
        }}
        onClick={(e) => e.stopPropagation()}
      ></div>
            <div
              className={styles.specialRatesPopup}
               style={{
    position: "fixed",
    top: `${specialRatesPosition.top}px`,
    left: `${specialRatesPosition.left}px`,
    width: `${specialRatesPosition.width}px`,
  }}
              onClick={(e) => e.stopPropagation()}
            >

              {/* CLOSE */}
              <button
                type="button"
                className={styles.closeSpecialRates}
                onClick={() => setShowSpecialRates(false)}
                aria-label="Close special rates"
              >
                <X size={20} />
              </button>


              {/* TITLE */}
              <div className={styles.specialRatesTitle}>
                SPECIAL RATES/POINTS
              </div>


              {/* =================================================
                  LOWEST REGULAR RATE
              ================================================== */}
              <button
                type="button"
                className={styles.rateOption}
                onClick={() =>
                  setSelectedRate("Lowest Regular Rate")
                }
              >

                <span>
                  Lowest Regular Rate
                </span>

                <span
                  className={`${styles.radioCircle} ${
                    selectedRate === "Lowest Regular Rate"
                      ? styles.radioSelected
                      : ""
                  }`}
                >
                  {selectedRate ===
                    "Lowest Regular Rate" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>

              </button>


              {/* =================================================
                  CORP / PROMO CODE
              ================================================== */}
              <button
                type="button"
                className={styles.rateOption}
                onClick={() =>
                  setSelectedRate("Corp/Promo Code")
                }
              >

                <span>
                  Corp/Promo Code
                </span>

                <span
                  className={`${styles.radioCircle} ${
                    selectedRate === "Corp/Promo Code"
                      ? styles.radioSelected
                      : ""
                  }`}
                >
                  {selectedRate ===
                    "Corp/Promo Code" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>

              </button>


              {/* =================================================
                  SENIOR DISCOUNT
              ================================================== */}
              <button
                type="button"
                className={styles.rateOption}
                onClick={() =>
                  setSelectedRate("Senior Discount")
                }
              >

                <span>
                  Senior Discount
                </span>

                <span
                  className={`${styles.radioCircle} ${
                    selectedRate === "Senior Discount"
                      ? styles.radioSelected
                      : ""
                  }`}
                >
                  {selectedRate ===
                    "Senior Discount" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>

              </button>


              {/* =================================================
                  AAA / CAA
              ================================================== */}
              <button
                type="button"
                className={styles.rateOption}
                onClick={() =>
                  setSelectedRate("AAA/CAA")
                }
              >

                <span>
                  AAA/CAA
                </span>

                <span
                  className={`${styles.radioCircle} ${
                    selectedRate === "AAA/CAA"
                      ? styles.radioSelected
                      : ""
                  }`}
                >
                  {selectedRate === "AAA/CAA" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>

              </button>


              {/* =================================================
                  GOVERNMENT & MILITARY
              ================================================== */}
              <button
                type="button"
                className={styles.rateOption}
                onClick={() =>
                  setSelectedRate("Government & Military")
                }
              >

                <span>
                  Government &amp; Military
                </span>

                <span
                  className={`${styles.radioCircle} ${
                    selectedRate ===
                    "Government & Military"
                      ? styles.radioSelected
                      : ""
                  }`}
                >
                  {selectedRate ===
                    "Government & Military" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>

              </button>


              {/* =================================================
                  GROUP CODE
              ================================================== */}
              <button
                type="button"
                className={styles.rateOption}
                onClick={() =>
                  setSelectedRate("Group Code")
                }
              >

                <span>
                  Group Code
                </span>

                <span
                  className={`${styles.radioCircle} ${
                    selectedRate === "Group Code"
                      ? styles.radioSelected
                      : ""
                  }`}
                >
                  {selectedRate === "Group Code" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>
                

              </button>
              {/* GROUP CODE INPUT */}
{selectedRate === "Group Code" && (
  <div className={styles.groupCodeInputWrapper}>

    <div className={styles.groupCodeInputRow}>
      <input
        type="text"
        className={styles.groupCodeInput}
        placeholder="Enter Code"
        aria-label="Enter Group Code"
      />

      <button
        type="button"
        className={styles.clearGroupCode}
        onClick={(e) => {
          e.currentTarget
            .previousElementSibling
            .value = "";
        }}
        aria-label="Clear group code"
      >
        ×
      </button>
    </div>

  </div>
)}
          
              {/* =================================================
                  MARRIOTT BONVOY POINTS
              ================================================== */}
              <button
                type="button"
                className={styles.rateOption}
                onClick={() =>
                  setSelectedRate("Marriott Bonvoy Points")
                }
              >

                <span>
                  Marriott Bonvoy Points
                </span>

                <span
                  className={`${styles.radioCircle} ${
                    selectedRate ===
                    "Marriott Bonvoy Points"
                      ? styles.radioSelected
                      : ""
                  }`}
                >
                  {selectedRate ===
                    "Marriott Bonvoy Points" && (
                    <span className={styles.radioDot}></span>
                  )}
                </span>

              </button>


              {/* =================================================
                  DONE
              ================================================== */}
              <button
                type="button"
                className={styles.doneButton}
                onClick={() =>
                  setShowSpecialRates(false)
                }
              >
                Done
              </button>

            </div>

          </div>,

          document.body
        )}

    </section>
  );
};

export default BookingBar;